import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ActiveFilterChipProps {
  /** Display label inside the chip, e.g. "Pinellas" or "Radius 1.0 mi". */
  label: string;
  /** Called when the user clicks the × button. */
  onRemove: () => void;
  className?: string;
}

/**
 * `<ActiveFilterChip>` — pill rendering one active filter with a × button
 * that resets that filter to its default.
 *
 * UI-SPEC § 3.9 + § 9.3:
 * - `inline-flex items-center gap-1 h-7 px-2.5 bg-ocean-800/60 border
 *    border-foam-50/10 rounded-full text-sm`
 * - The × button has `p-2` invisible padding to satisfy the 44×44 hit
 *   target requirement even though the icon is 12px.
 * - Hover on the × ramps opacity 0.7 → 1 (UI-SPEC § 1.4).
 *
 * The component does not interpret the filter — the parent controls
 * which `FilterState` field to reset on click.
 */
export function ActiveFilterChip({
  label,
  onRemove,
  className,
}: ActiveFilterChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-7 pl-2.5 bg-ocean-800/60 border border-foam-50/10 rounded-full text-sm text-foam-50',
        className,
      )}
    >
      {label}
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={onRemove}
        className={cn(
          'inline-flex items-center justify-center w-11 h-11 -my-2 -mr-1.5 p-2 rounded-full',
          'opacity-70 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
          'transition-opacity duration-100',
        )}
      >
        <X aria-hidden className="w-3 h-3" />
      </button>
    </span>
  );
}
